import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import { requiredWizardGuardNames } from '../wizard/create-wizard';
import { interpretWizard, type WizardActor } from '../wizard/interpret-wizard';
import type {
  Wizard,
  WizardImplementations,
  WizardPersistenceAdapter,
  WizardSnapshot,
} from '../wizard/types';

export type UseWizardResult<TStep extends string> = WizardSnapshot<TStep> & {
  /** Navigation actions return whether the transition was taken — see `WizardActor`. */
  next: () => boolean;
  back: () => boolean;
  skip: () => boolean;
  goTo: (step: TStep) => boolean;
  reset: () => void;
};

/**
 * Subscribe a React component to a wizard.
 *
 * The actor is created once and kept stable; `guards` / `onTransition` /
 * `persistence` are read through a ref so the latest closures (form state,
 * atoms, feature flags) are used without recreating the actor. The snapshot is
 * read via `useSyncExternalStore` with the wizard's initial snapshot as the
 * server snapshot (no hydration mismatch), and the `enabled` guards are
 * re-evaluated after every commit — a conditional step appearing or
 * disappearing reconciles automatically, no manual `refresh()` needed.
 *
 * Note: whether persistence is wired is decided on the first render — provide
 * the adapter from the start (its methods may change identity freely after).
 *
 * @returns the snapshot spread together with stable `next`/`back`/`skip`/`goTo`.
 */
export function useWizard<TStep extends string>(
  wizard: Wizard<TStep>,
  impls: WizardImplementations<TStep> = {}
): UseWizardResult<TStep> {
  const implsRef = useRef(impls);
  // Synced in an effect (not during render) so a discarded concurrent render
  // can't leak its impls into the ref — same contract as useStateMachine.
  useEffect(() => {
    implsRef.current = impls;
  });

  const actorRef = useRef<WizardActor<TStep> | null>(null);
  if (actorRef.current === null) {
    const guards: NonNullable<WizardImplementations<TStep>['guards']> = {};
    for (const key of requiredWizardGuardNames(wizard)) {
      guards[key] = () => {
        const impl = implsRef.current.guards?.[key];
        if (!impl) throw new Error(`[@heidi/flow] Guard "${key}" has no implementation.`);
        return impl();
      };
    }
    const persistence: WizardPersistenceAdapter<TStep> | undefined = impls.persistence
      ? {
          save: (snapshot) => implsRef.current.persistence?.save?.(snapshot),
          restore: () => implsRef.current.persistence?.restore() ?? null,
          clear: () => implsRef.current.persistence?.clear?.(),
        }
      : undefined;
    actorRef.current = interpretWizard(wizard, {
      guards,
      persistence,
      onTransition: (record) => implsRef.current.onTransition?.(record),
      input: impls.input, // read once at creation, like the actor itself
    });
  }
  const actor = actorRef.current;

  const subscribe = useMemo(
    () => (onStoreChange: () => void) => {
      const unsubscribe = actor.subscribe(onStoreChange);
      actor.start();
      return () => {
        unsubscribe();
        actor.stop();
      };
    },
    [actor]
  );

  const snapshot = useSyncExternalStore(
    subscribe,
    actor.getSnapshot,
    actor.getInitialSnapshot
  );

  // Re-evaluate `enabled` guards after every commit: the values they close
  // over (flags, query data) change by re-render, not by wizard event.
  useEffect(() => {
    actor.refresh();
  });

  const actions = useMemo(
    () => ({
      next: () => actor.next(),
      back: () => actor.back(),
      skip: () => actor.skip(),
      goTo: (step: TStep) => actor.goTo(step),
      reset: () => actor.reset(),
    }),
    [actor]
  );

  return useMemo(() => ({ ...snapshot, ...actions }), [snapshot, actions]);
}
